import { useState } from "react";
import {
  Button,
  Card,
  Checkbox,
  CountrySelect,
  DEFAULT_COUNTRY_CODE,
  Icon,
  Input,
  Modal,
  NumberInput,
  Select,
  Space,
  Spin,
  Switch,
  Table,
  Text,
  Textarea,
  useToast,
} from "@app/ui";
import {
  createFacility,
  deleteFacility,
  getFacility,
  listCertifications,
  listFacilities,
  setFacilityCertifications,
  updateFacility,
} from "../api/dpp.js";
import type { Certification, Facility, FacilityBody } from "../api/dpp.js";
import { useAsync } from "../api/useAsync.js";
import { ErrorAlert, useErrorText } from "../errors/index.js";
import { useT } from "../i18n.js";
import { useGoogleMaps } from "../maps/loader.js";
import { MapPicker } from "../maps/MapPicker.js";
import { PinsMap } from "../maps/PinsMap.js";
import { certValidity } from "./certValidity.js";

/** Numeric form fields ride as strings while editing; converted on submit. */
interface FacilityDraft {
  name: string;
  countryIso: string;
  city: string;
  lat: string;
  lng: string;
  employeeCount: string;
  renewableEnergyPct: string;
  waterRecycling: boolean;
  wastewaterTreatment: boolean;
  practicesNote: string;
  identifierScheme: "GLN" | "internal" | "";
  identifierValue: string;
  certificationIds: string[];
}

const EMPTY_FACILITY: FacilityDraft = {
  name: "",
  countryIso: DEFAULT_COUNTRY_CODE,
  city: "",
  lat: "",
  lng: "",
  employeeCount: "",
  renewableEnergyPct: "",
  waterRecycling: false,
  wastewaterTreatment: false,
  practicesNote: "",
  identifierScheme: "",
  identifierValue: "",
  certificationIds: [],
};

/** Draft coordinate string → number for the map picker (null = unset/invalid). */
function draftCoord(v: string): number | null {
  if (v.trim() === "") return null;
  const n = Number(v);
  return Number.isNaN(n) ? null : n;
}

function facilityBody(d: FacilityDraft): FacilityBody {
  const num = (v: string) => (v.trim() === "" ? undefined : Number(v));
  return {
    name: d.name.trim(),
    countryIso: d.countryIso.trim().toUpperCase(),
    ...(d.city.trim() ? { city: d.city.trim() } : {}),
    ...(num(d.lat) !== undefined ? { lat: num(d.lat)! } : {}),
    ...(num(d.lng) !== undefined ? { lng: num(d.lng)! } : {}),
    ...(num(d.employeeCount) !== undefined ? { employeeCount: num(d.employeeCount)! } : {}),
    ...(num(d.renewableEnergyPct) !== undefined ? { renewableEnergyPct: num(d.renewableEnergyPct)! } : {}),
    waterRecycling: d.waterRecycling,
    wastewaterTreatment: d.wastewaterTreatment,
    ...(d.practicesNote.trim() ? { practicesNote: d.practicesNote.trim() } : {}),
    ...(d.identifierScheme && d.identifierValue.trim()
      ? { identifierScheme: d.identifierScheme, identifierValue: d.identifierValue.trim() }
      : {}),
  };
}

/**
 * The org's production sites: map of pinned sites, the list, and the create/edit
 * dialog. The dialog offers the org's certificates to attach, fetched fresh each
 * time it opens so a certificate added in the neighbouring card is there.
 */
export function FacilitiesCard({ orgId, canWrite }: { orgId: string; canWrite: boolean }) {
  const t = useT();
  const toast = useToast();
  const errorText = useErrorText();
  const mapsNs = useGoogleMaps();
  const { data: facilities, error, loading, reload } = useAsync(() => listFacilities(orgId), [orgId]);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [draft, setDraft] = useState<FacilityDraft>(EMPTY_FACILITY);
  const [certOptions, setCertOptions] = useState<Certification[]>([]);
  const [saving, setSaving] = useState(false);

  const openModal = async (facility?: Facility) => {
    try {
      const [certs, detail] = await Promise.all([
        listCertifications(orgId),
        facility ? getFacility(orgId, facility.id) : Promise.resolve(null),
      ]);
      setCertOptions(certs);
      setEditId(facility?.id ?? null);
      setDraft(
        detail
          ? {
              name: detail.name,
              countryIso: detail.countryIso,
              city: detail.city ?? "",
              lat: detail.lat === null ? "" : String(detail.lat),
              lng: detail.lng === null ? "" : String(detail.lng),
              employeeCount: detail.employeeCount === null ? "" : String(detail.employeeCount),
              renewableEnergyPct: detail.renewableEnergyPct === null ? "" : String(detail.renewableEnergyPct),
              identifierScheme: detail.identifierScheme ?? "",
              identifierValue: detail.identifierValue ?? "",
              waterRecycling: detail.waterRecycling === true,
              wastewaterTreatment: detail.wastewaterTreatment === true,
              practicesNote: detail.practicesNote ?? "",
              certificationIds: detail.certifications.map((c) => c.id),
            }
          : EMPTY_FACILITY,
      );
      setOpen(true);
    } catch (e) {
      toast.error(errorText(e));
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const saved = editId
        ? await updateFacility(orgId, editId, facilityBody(draft))
        : await createFacility(orgId, facilityBody(draft));
      await setFacilityCertifications(orgId, saved.id, draft.certificationIds);
      toast.success(t("facilities.saved"));
      setOpen(false);
      reload();
    } catch (e) {
      toast.error(errorText(e));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id: string) => {
    try {
      await deleteFacility(orgId, id);
      toast.success(t("facilities.deleted"));
      reload();
    } catch (e) {
      toast.error(errorText(e));
    }
  };

  const pins = (facilities ?? []).flatMap((f) =>
    f.lat !== null && f.lng !== null ? [{ lat: f.lat, lng: f.lng, label: f.name }] : [],
  );

  return (
    <Card
      title={t("facilities.facilities")}
      extra={
        canWrite && (
          <Button variant="primary" icon={<Icon name="plus" size={16} />} onClick={() => void openModal()}>
            {t("facilities.new")}
          </Button>
        )
      }
    >
      {error != null ? (
        <ErrorAlert error={error} onRetry={reload} />
      ) : loading && !facilities ? (
        <Spin />
      ) : (
        <>
          {mapsNs != null && pins.length > 0 && (
            <div style={{ marginBottom: "var(--az-sp-4)" }}>
              <PinsMap pins={pins} height={320} />
            </div>
          )}
          <Table
            rowKey={(f) => f.id}
            empty={t("facilities.empty")}
            columns={[
              { key: "name", title: t("facilities.name"), render: (f) => f.name },
              {
                key: "location",
                title: t("facilities.location"),
                render: (f) => [f.city, f.countryIso].filter(Boolean).join(", "),
              },
              {
                key: "identifier",
                title: t("facilities.identifier"),
                render: (f) =>
                  f.identifierScheme && f.identifierValue ? (
                    <Text code>
                      {f.identifierScheme === "GLN" ? "GLN " : ""}
                      {f.identifierValue}
                    </Text>
                  ) : (
                    "—"
                  ),
              },
              { key: "employees", title: t("facilities.employees"), render: (f) => f.employeeCount ?? "—" },
              {
                key: "renewable",
                title: t("facilities.renewable"),
                render: (f) => (f.renewableEnergyPct === null ? "—" : `${f.renewableEnergyPct}%`),
              },
              {
                key: "actions",
                title: t("common.actions"),
                render: (f) =>
                  canWrite ? (
                    <Space>
                      <Button size="sm" onClick={() => void openModal(f)}>
                        {t("common.edit")}
                      </Button>
                      <Button size="sm" variant="danger" onClick={() => void remove(f.id)}>
                        {t("common.remove")}
                      </Button>
                    </Space>
                  ) : null,
              },
            ]}
            data={facilities ?? []}
          />
        </>
      )}

      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title={editId ? t("facilities.edit") : t("facilities.new")}
        onOk={() => void save()}
        okText={t("common.save")}
        cancelText={t("common.cancel")}
        confirmLoading={saving}
        width={640}
      >
        <Space direction="vertical" size="md" style={{ width: "100%" }}>
          <Input
            label={t("facilities.name")}
            required
            value={draft.name}
            onChange={(e) => setDraft({ ...draft, name: e.target.value })}
          />
          <Space wrap>
            <div style={{ width: 240 }}>
              <CountrySelect
                label={t("facilities.country")}
                required
                placeholder={t("facilities.countryPlaceholder")}
                value={draft.countryIso}
                onChange={(v) => setDraft({ ...draft, countryIso: v })}
              />
            </div>
            <Input
              label={t("facilities.city")}
              value={draft.city}
              onChange={(e) => setDraft({ ...draft, city: e.target.value })}
            />
            <NumberInput
              label={t("facilities.employees")}
              value={draft.employeeCount}
              onChange={(v) => setDraft({ ...draft, employeeCount: v })}
            />
          </Space>
          {/* Exact location: map pick and the numeric inputs stay in two-way sync. */}
          <MapPicker
            lat={draftCoord(draft.lat)}
            lng={draftCoord(draft.lng)}
            onChange={(lat, lng) => setDraft({ ...draft, lat: String(lat), lng: String(lng) })}
          />
          <Space wrap>
            <NumberInput
              label={t("facilities.lat")}
              value={draft.lat}
              onChange={(v) => setDraft({ ...draft, lat: v })}
            />
            <NumberInput
              label={t("facilities.lng")}
              value={draft.lng}
              onChange={(v) => setDraft({ ...draft, lng: v })}
            />
            <NumberInput
              label={t("facilities.renewable")}
              min={0}
              max={100}
              suffix="%"
              value={draft.renewableEnergyPct}
              onChange={(v) => setDraft({ ...draft, renewableEnergyPct: v })}
            />
          </Space>
          <Space wrap>
            <Switch
              checked={draft.waterRecycling}
              onChange={(e) => setDraft({ ...draft, waterRecycling: e.target.checked })}
            >
              {t("facilities.waterRecycling")}
            </Switch>
            <Switch
              checked={draft.wastewaterTreatment}
              onChange={(e) => setDraft({ ...draft, wastewaterTreatment: e.target.checked })}
            >
              {t("facilities.wastewaterTreatment")}
            </Switch>
          </Space>
          <Textarea
            label={t("facilities.practices")}
            rows={2}
            value={draft.practicesNote}
            onChange={(e) => setDraft({ ...draft, practicesNote: e.target.value })}
          />
          {/* Unique facility identifier (GLN preferred) — published on every stage naming this site. */}
          <Space wrap>
            <div style={{ width: 200 }}>
              <Select
                label={t("facilities.identifierScheme")}
                options={[
                  { label: t("facilities.identifierNone"), value: "" },
                  { label: "GS1 GLN", value: "GLN" },
                  { label: t("facilities.identifierInternal"), value: "internal" },
                ]}
                value={draft.identifierScheme}
                onChange={(v) => setDraft({ ...draft, identifierScheme: v as "GLN" | "internal" | "" })}
              />
            </div>
            <Input
              label={t("facilities.identifierValue")}
              help={t("facilities.identifierHelp")}
              value={draft.identifierValue}
              onChange={(e) => setDraft({ ...draft, identifierValue: e.target.value })}
            />
          </Space>
          {certOptions.length > 0 && (
            <div>
              <Text strong>{t("facilities.certifications")}</Text>
              <Space direction="vertical" size="sm" style={{ width: "100%", marginTop: 4 }}>
                {certOptions.map((c) => (
                  <Checkbox
                    key={c.id}
                    checked={draft.certificationIds.includes(c.id)}
                    onChange={(e) =>
                      setDraft({
                        ...draft,
                        certificationIds: e.target.checked
                          ? [...draft.certificationIds, c.id]
                          : draft.certificationIds.filter((id) => id !== c.id),
                      })
                    }
                  >
                    {c.scheme}
                    {c.validUntil ? ` (${certValidity(c, t("certs.noExpiry"))})` : ""}
                  </Checkbox>
                ))}
              </Space>
            </div>
          )}
        </Space>
      </Modal>
    </Card>
  );
}
